import React from "react";
import type WidgetScoreDataDto from "../../../types/dtos/content-dashboard/WidgetScoreDataDto";
import Tooltip from "../../common/Tooltip";

interface SparklineProps {
  /** Array of score data points to plot */
  trendScores: WidgetScoreDataDto[];
  /** Currently selected score index */
  selectedIndex: number;
  /** Callback when a point is clicked */
  onPointClick: (index: number) => void;
  /** Chart color (hex with #) */
  color: string;
}

// Format date for display (e.g., "Nov '25")
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const month = date.toLocaleDateString("en-US", { month: "short" });
  const year = date.getFullYear().toString().slice(-2);
  return `${month} '${year}`;
};

// Format full date for tooltip (e.g., "November 24, 2025")
const formatFullDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

const Sparkline: React.FC<SparklineProps> = ({
  trendScores,
  selectedIndex,
  onPointClick,
  color,
}) => {
  // Filter out any scores that are not numbers
  const numericScores = trendScores.filter(
    (s) => !isNaN(Number(s.displayValue))
  );

  // Reverse scores to show oldest -> newest (left to right)
  const reversedScores = [...trendScores].reverse();

  // Convert scores to percentages for consistent plotting
  // Each score uses its own scale configuration
  const scores = reversedScores.map((s) => {
    if (
      s.useScale &&
      s.scaleScore !== null &&
      s.scaleScore !== undefined &&
      s.scaleMax !== null &&
      s.scaleMax !== undefined
    ) {
      return (s.scaleScore / s.scaleMax) * 100;
    }
    return Number(s.displayValue);
  });

  // Use viewBox for responsive scaling
  const viewBoxWidth = 200;
  const viewBoxHeight = 68; // Increased to accommodate labels
  const paddingX = 20; // Horizontal padding to prevent label clipping
  const paddingY = 8; // Vertical padding for the chart
  const chartHeight = 40; // Height of the actual chart area
  const maxScore = Math.max(...scores, 100);
  const minScore = Math.min(...scores, 0);

  // Calculate points for the line
  const points = scores.map((score, index) => {
    const x =
      paddingX + (index / (scores.length - 1)) * (viewBoxWidth - 2 * paddingX);
    const y =
      chartHeight -
      paddingY -
      ((score - minScore) / (maxScore - minScore)) *
        (chartHeight - 2 * paddingY);
    return { x, y, index };
  });

  // Create path
  const pathData = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`)
    .join(" ");

  return (
    <div className="mt-0 mb-2 px-2">
      <div className="relative">
        <svg
          viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
          className="w-full h-auto max-w-[200px] mx-auto"
          style={{ display: "block" }}
          preserveAspectRatio="xMidYMid meet"
        >
          {/* Line */}
          <path
            d={pathData}
            fill="none"
            stroke={color}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {/* Points */}
          {points.map((point, index) => {
            // Reverse index to match original trendScores array
            const originalIndex = trendScores.length - 1 - index;
            const isSelected = selectedIndex === originalIndex;

            return (
              <g key={index}>
                <circle
                  cx={point.x}
                  cy={point.y}
                  r={isSelected ? 5 : 3}
                  fill={isSelected ? color : "white"}
                  stroke={color}
                  strokeWidth="2"
                  className="cursor-pointer hover:r-4 transition-all"
                  onClick={() => onPointClick(originalIndex)}
                  style={{ cursor: "pointer" }}
                />
              </g>
            );
          })}
        </svg>
        {/* Date labels overlay with Tooltips */}
        <div
          className="absolute inset-0 flex items-end justify-between pointer-events-none"
          style={{ paddingBottom: "8px" }}
        >
          {points.map((point, index) => {
            const originalIndex = trendScores.length - 1 - index;
            const isSelected = selectedIndex === originalIndex;

            return (
              <div
                key={index}
                className="pointer-events-auto"
                style={{
                  position: "absolute",
                  left: `${(point.x / viewBoxWidth) * 100}%`,
                  transform:
                    index === 0
                      ? "translateX(0)"
                      : index === points.length - 1
                      ? "translateX(-100%)"
                      : "translateX(-50%)",
                }}
              >
                <Tooltip
                  content={formatFullDate(reversedScores[index].scoreDate)}
                  appearAt="top"
                  triggerElement={
                    <button
                      onClick={() => onPointClick(originalIndex)}
                      className="text-xs transition-colors cursor-pointer focus:outline-none whitespace-nowrap"
                      style={{
                        color: isSelected ? "#111827" : "#6B7280",
                        fontWeight: isSelected ? 600 : 500,
                        fontSize: "11px",
                      }}
                    >
                      {formatDate(reversedScores[index].scoreDate)}
                    </button>
                  }
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Sparkline;
